import React, { useEffect, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Dimmer } from "semantic-ui-react";
import Table from "../../../components/Table/Table";
import { editActions } from "../../../actions/editActions";
import { mainActions } from "../../../actions/mainActions";
import { pdApiActions } from "../../../actions/pdApiActions";
import { isEditActiveSelector, editedObjectIdSelector } from "../../../reducers/editReducer";
import _ from "lodash";
import EditEnumCommon from "../../Editing/EditEnumCommon";
import { Filter } from "../../../components/Table/Migration/LegacyTypes";

type OwnProps = {
  subMenuRoute: State.AnySubMenu | State.AnyChildItem;
  toggleModal: () => void;
  dataFieldName: keyof State.PdApi | null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  editor?: any;
  setColumnFilters: (
    subMenuRoute: State.AnySubMenu | State.AnyChildItem,
    columnFilter: Filter[]
  ) => void;
};

const EnumTableCommon: React.FC<OwnProps> = props => {
  const {
    subMenuRoute,
    toggleModal,
    dataFieldName,
    editor = EditEnumCommon,
    setColumnFilters
  } = props;
  const dispatch = useDispatch();

  const columns = useSelector((state: State.Root) => state.columns);
  const pdApi = useSelector((state: State.Root) => state.pdApi);
  const mainState = useSelector((state: State.Root) => state.main);
  const isEditActive = useSelector((state: State.Root) => isEditActiveSelector(state.editView));
  const editedObjectId = useSelector((state: State.Root) => editedObjectIdSelector(state.editView));
  const isLoading = useSelector(
    (state: State.Root) => state.loadingStatus.isLoadingEventEnumerations
  );

  useEffect(() => {
    dispatch(mainActions.setLastEditedObjectId(undefined));
  }, [subMenuRoute, dispatch]);

  useEffect(() => {
    handleFilterChangeDebounced = _.debounce(handleFilterChangeDebounced, 500);
    return () => {
      dispatch(mainActions.setLastEditedObjectId(undefined));
    };
  }, [dispatch]);

  useEffect(() => {
    if (!pdApi[subMenuRoute.split(" ").join("") as keyof State.PdApi]) {
      dispatch(pdApiActions.fetchEscalatorEvent(subMenuRoute));
      if (subMenuRoute === "Escalator Event Types") {
        if (!pdApi.EscalatorEventClasses) {
          dispatch(pdApiActions.fetchEscalatorEvent("Escalator Event Classes"));
        }
      }
    }
  }, [subMenuRoute, dispatch]);

  let handleFilterChangeDebounced = useCallback(
    (changedFilters: Filter[]) => {
      setColumnFilters(subMenuRoute, changedFilters);
    },
    [setColumnFilters, subMenuRoute]
  );

  const isEnumerationSelected = useCallback(
    (enumeration: Api.AnyItem): boolean => {
      const editedId = editedObjectId ?? mainState.lastEditedObjectId;
      if (editedId !== undefined) {
        if (
          enumeration.enumValue === editedId ||
          enumeration.value === editedId ||
          enumeration.id === editedId ||
          enumeration.boardTypeId === editedId
        ) {
          return true;
        }
      }
      return false;
    },
    [editedObjectId, mainState.lastEditedObjectId]
  );

  if (!dataFieldName) {
    return <div>Error: missing dataFieldName</div>;
  }

  let selectedData: Api.AnyItem[];
  if (Array.isArray(pdApi[dataFieldName])) {
    selectedData = pdApi[dataFieldName] as Api.AnyItem[];
  } else {
    selectedData = _.values(pdApi[dataFieldName]);
  }

  if (
    subMenuRoute === "Escalator Event Types" &&
    pdApi.EscalatorEventClasses &&
    pdApi.EscalatorEventTypes
  ) {
    pdApi.EscalatorEventTypes.forEach((row: Api.EventEnumeration) => {
      if (pdApi.EscalatorEventClasses) {
        const eventClass = pdApi.EscalatorEventClasses.findIndex(
          (eventClass: Api.EventEnumeration) => row.escalatorEventClassId === eventClass.enumValue
        );
        row.eventClassEnumName =
          eventClass === -1
            ? "None"
            : pdApi.EscalatorEventClasses[eventClass].enumName +
              " [" +
              pdApi.EscalatorEventClasses[eventClass].enumValue +
              "]";
      }
    });
    selectedData = pdApi.EscalatorEventTypes;
  }

  const { editSize, pageSize, isShortenTextEnabled } = mainState;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const rows: any[] = selectedData;
  const selectedColumns: State.Column[] = columns[subMenuRoute as keyof State.ColumnMap];

  const startingPage = mainState.lastEditedObjectId
    ? (rows.findIndex(enumeration => isEnumerationSelected(enumeration)) / mainState.pageSize) >> 0
    : 0;

  return (
    <div className="flex-1">
      <div className="flex-1 IOEventEnumTable">
        <Table
          key={subMenuRoute} // this forces re-render when view changes to reset page to 0
          startingPage={startingPage}
          rows={rows}
          columns={selectedColumns}
          isHidingTextActive={isShortenTextEnabled}
          handleRowSelection={row => dispatch(editActions.select(row))}
          isRowSelected={isEnumerationSelected}
          pageSize={pageSize}
          handleFilterChange={handleFilterChangeDebounced}
          loading={isLoading}
          loadingText={"Fetching " + subMenuRoute}
        />
      </div>
      {isEditActive && (
        <Dimmer active={true} page={true}>
          <div
            className="edit-view"
            style={{ flex: editSize, width: editSize === 0.3 ? "500px" : "1000px" }}
          >
            {React.createElement(editor, {
              ...props,
              toggleModal,
              dataFieldName
            })}
          </div>
        </Dimmer>
      )}
    </div>
  );
};

export default EnumTableCommon;
